import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { ToastrService } from 'ngx-toastr';

import { ApiService, BackendType } from './api.service';
import { IExternalUser, IExternalUserResponse } from '../models/api_models';

@Injectable({
  providedIn: 'root',
})
export class ExternalUserService {
  private apiService = inject(ApiService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private translocoService = inject(TranslocoService);

  private pageCache = new Map<number, IExternalUser[]>();
  private currentPageSize = 0;
  private currentSearchKey = '';
  private currentSearchTerms: string[] = [];

  externalUsers = signal<IExternalUser[] | undefined>(undefined);
  totalExternalUsers = signal<number>(0);
  loadingExternalUsers = signal<boolean>(false);
  savingExternalUser = signal<boolean>(false);
  deletingExternalUser = signal<boolean>(false);

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401 || error.status === 403) {
      this.router.navigate(['unauthorized']);
    } else if (error.status === 404) {
      this.router.navigate(['404']);
    } else if (error.error?.detail) {
      this.toastr.error(error.error?.detail);
    } else {
      this.toastr.error(this.translocoService.translate('switchboard.errors.genericApiError'));
    }
  }

  getExternalUsers(page = 1, pageSize = 10, searchTerms: string[] = []) {
    const searchKey = JSON.stringify(searchTerms);

    if (
      this.currentSearchKey !== searchKey ||
      (this.currentPageSize && this.currentPageSize !== pageSize)
    ) {
      this.pageCache.clear();
    }
    this.currentSearchKey = searchKey;
    this.currentSearchTerms = searchTerms;
    this.currentPageSize = pageSize;

    const cached = this.pageCache.get(page);
    if (cached) {
      this.externalUsers.set(cached);
      return;
    }

    const requestKey = `${searchKey}-${page}-${pageSize}`;
    this.loadingExternalUsers.set(true);
    const params: Record<string, string | number | string[] | boolean> = {
      currentPage: page,
      pageSize,
    };
    if (searchTerms.length > 0) {
      params['searchTerms'] = searchTerms;
    }

    this.apiService
      .get<IExternalUserResponse>(
        'api/v1/external-user',
        {
          headers: {
            'Content-Type': 'application/json',
          },
          params,
        },
        BackendType.SB,
      )
      .subscribe({
        next: response => {
          if (`${searchKey}-${page}-${pageSize}` !== requestKey) {
            return;
          }
          this.externalUsers.set(response.data);
          this.totalExternalUsers.set(response.count);
          this.pageCache.set(page, response.data);
        },
        error: (error: HttpErrorResponse) => {
          this.loadingExternalUsers.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.loadingExternalUsers.set(false);
        },
      });
  }

  clearCache() {
    this.pageCache.clear();
  }

  createExternalUser(user: IExternalUser) {
    this.savingExternalUser.set(true);
    console.log('ok');
    this.apiService
      .post<IExternalUserResponse>(
        'api/v1/external-user',
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            name: user.name,
            email: user.email,
            phone: user.phone,
            designation: user.designation,
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: () => {
          this.toastr.success(this.translocoService.translate('contactManagement.userSaved'));
          this.clearCacheAndReload();
        },
        error: (error: HttpErrorResponse) => {
          this.savingExternalUser.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.savingExternalUser.set(false);
        },
      });
  }

  updateExternalUser(user: IExternalUser) {
    this.savingExternalUser.set(true);
    this.apiService
      .put<IExternalUserResponse>(
        `api/v1/external-user/${user.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            name: user.name,
            email: user.email,
            phone: user.phone,
            designation: user.designation,
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: () => {
          this.savingExternalUser.set(false);
          this.toastr.success(this.translocoService.translate('contactManagement.userSaved'));
          this.clearCacheAndReload();
        },
        error: (error: HttpErrorResponse) => {
          this.savingExternalUser.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.savingExternalUser.set(false);
        },
      });
  }

  deleteExternalUser(id: number) {
    this.deletingExternalUser.set(true);
    this.apiService
      .delete<unknown>(
        `api/v1/external-user/${id}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
        BackendType.SB,
      )
      .subscribe({
        next: () => {
          this.deletingExternalUser.set(false);
          this.toastr.success(this.translocoService.translate('contactManagement.userDeleted'));
          this.clearCacheAndReload();
        },
        error: (error: HttpErrorResponse) => {
          this.deletingExternalUser.set(false);
          this.handleError(error);
        },
        complete: () => {
          this.deletingExternalUser.set(false);
        },
      });
  }

  private clearCacheAndReload() {
    this.clearCache();
    this.getExternalUsers(1, this.currentPageSize || 10, this.currentSearchTerms);
  }
}
